'use strict'

// CRAのwebpackが jsep の exports を解決しない場合があるため、dist を直接指定
import jsep from 'jsep'

// 画面の「かつ」「または」で挿入される AND / OR を二項演算子として扱う
jsep.addBinaryOp('OR', 1) // || と同じ優先度（低い）
jsep.addBinaryOp('AND', 2) // && と同じ優先度

/** 条件式として許可しないASTのtype（JSEPのルートノードが複数式・カンマ区切り等になった場合） */
const INVALID_AST_ROOT_TYPES = ['Compound', 'SequenceExpression']

/**
 * 文字列を引用符で囲み、内部の二重引用符をエスケープする Jsepが引数を式として解釈せず文字列リテラルとして扱うために使用する
 *
 * @param {string} s - 囲む文字列
 * @returns {string} ... で囲んだ文字列（内部の " は " にエスケープ）
 */
function quoteArg(s) {
  if (s == null) return '""'
  const escaped = String(s).replace(/\\/g, '\\\\').replace(/"/g, '\\"')
  return `"${escaped}"`
}

/**
 * 条件式の表記を変換する。
 *
 * 1. `RANK([分類.項目])` → `col("RANK([分類.項目])")`（判定用 DataFrame の RANK 列を col で参照）
 * 2. `[分類.項目]` → `col("分類", "項目")`
 *
 * RANK は通常の `[...]` 変換より先に処理する（`RANK(col(...))` にならないようにする）。
 *
 * @param {string} condition - 変換前の条件式（画面の [分類.項目] 形式）
 * @returns {string} 変換後の条件式（col("分類", "項目") 形式）
 */
export function convertConditionToColFormat(condition) {
  if (!condition || typeof condition !== 'string') return condition

  const rankExprs = []
  const rankPattern = /RANK\(\[([^\]]+)\]\)/g
  let result = condition.replace(rankPattern, (_, inner) => {
    const token = `__RANK_${rankExprs.length}__`
    rankExprs.push(`RANK([${inner.trim()}])`)
    return token
  })

  const pattern = /\[([^\]]+)\]/g
  result = result.replace(pattern, (_, content) => {
    // ピリオドで分割（最大2つ）
    const parts = content.split(/\./, 2).map((p) => p.trim())
    if (parts.length === 2 && parts[0] !== '' && parts[1] !== '') {
      return `col(${quoteArg(parts[0])}, ${quoteArg(parts[1])})`
    }
    return `col(${quoteArg(content.trim())})`
  })

  rankExprs.forEach((rankExpr, i) => {
    result = result.replace(`__RANK_${i}__`, `col(${quoteArg(rankExpr)})`)
  })

  return result
}

/**
 * 条件式をJsepでパースしてASTオブジェクトを返す パースに失敗した場合は例外を投げる
 *
 * @param {string} condition - 条件式（col(分類, 項目) 形式）
 * @returns {Object} ASTオブジェクト（JSON化可能なプレーンオブジェクト）
 * @throws パースエラー時
 */
export function parseConditionToAst(condition) {
  const expr = (condition || '').trim()
  if (!expr) {
    throw new Error('条件式が空です')
  }
  const ast = jsep(expr)
  const rootType = ast?.type
  if (rootType && INVALID_AST_ROOT_TYPES.includes(rootType)) {
    throw new Error(`条件式が不正です（複数式やカンマ区切りは使用できません: type=${rootType}）`)
  }
  return toPlainObject(ast)
}

/**
 * JsepのASTノードをJSONシリアライズ可能なプレーンオブジェクトに変換
 *
 * @param {Object} node - JsepのASTノード
 * @returns {Object} プレーンオブジェクト
 */
function toPlainObject(node) {
  if (node === null || node === undefined) return node
  if (typeof node !== 'object') return node
  const plain = {}
  for (const key of Object.keys(node)) {
    const value = node[key]
    if (Array.isArray(value)) {
      plain[key] = value.map((item) => toPlainObject(item))
    } else if (value !== null && typeof value === 'object') {
      plain[key] = toPlainObject(value)
    } else {
      plain[key] = value
    }
  }
  return plain
}
